import { Plus, Upload, Sparkles, Zap, Brain } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { AVAILABLE_MODELS } from '../../types/models';
import { cn } from '../../lib/utils';

interface ComposerActionsProps {
  onUploadToResources: () => void;
  currentModel: string;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  isCompact?: boolean;
}

export function ComposerActions({
  onUploadToResources,
  currentModel,
  onModelChange,
  disabled = false,
  isCompact = false
}: ComposerActionsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          className={cn(
            "rounded-full transition-all",
            isCompact ? "h-7 w-7" : "h-8 w-8"
          )}
          aria-label="Composer actions"
        >
          <Plus className={cn(
            "transition-transform duration-200",
            isCompact ? "h-4 w-4" : "h-5 w-5"
          )} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56"
        side="top"
        align="start"
        sideOffset={8}
      >
        {/* Model Selector Section - Ultra Compact */}
        <DropdownMenuLabel className="flex items-center gap-1.5 pb-1.5 pt-2">
          <Sparkles className="h-3 w-3 text-primary" />
          <span className="text-xs font-medium">AI Model</span>
        </DropdownMenuLabel>
        
        <DropdownMenuRadioGroup value={currentModel} onValueChange={onModelChange}>
          {AVAILABLE_MODELS.map((model) => {
            const isSelected = currentModel === model.id;
            const Icon = model.category === 'flash' ? Zap : Brain;
            
            return (
              <DropdownMenuRadioItem 
                key={model.id} 
                value={model.id}
                className="py-1.5"
                disabled={disabled}
              >
                <Icon className={cn(
                  "mr-2 h-3.5 w-3.5",
                  isSelected ? "text-primary" : "text-muted-foreground"
                )} />
                <span className={cn(
                  "flex-1",
                  isSelected && "font-medium"
                )}>
                  {model.name}
                </span>
                {model.category === 'flash' ? (
                  <span className="ml-2 text-xs text-muted-foreground">Fast</span>
                ) : (
                  <span className="ml-2 text-xs text-muted-foreground">Pro</span>
                )}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        {/* Actions Section */}
        {!isCompact && (
          <DropdownMenuItem
            onClick={onUploadToResources}
            disabled={disabled}
            className="py-1.5"
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            <span className="flex-1">Upload to resources</span>
            <span className="text-xs text-muted-foreground">⌘U</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ComposerActionsMinimal({
  currentModel,
  onModelChange,
  disabled = false
}: Omit<ComposerActionsProps, 'onUploadToResources' | 'isCompact'>) {
  return (
    <ComposerActions
      onUploadToResources={() => {}}
      currentModel={currentModel}
      onModelChange={onModelChange}
      disabled={disabled}
      isCompact={true}
    />
  );
}